import { roundDecimal } from "components/helper/numberHelper";
import { useEffect, useId, useState } from "react";

import { DistributionCaseProposalProps } from "../types";
import SelectionBox from "./SelectionBox";
import {
    DescriptionText,
    getBoxSize,
    Input,
    LabelGroup,
    ProposalImage,
    StyledPropositionsText,
    StyledWrapper,
    TextWrapper,
} from "./DistributionCaseProposal.styles";

const DistributionCaseProposal = ({
    boxPosition = "side",
    color,
    currentRef,
    description,
    handleBlur,
    handleKeyUp,
    hintId,
    image,
    isActive,
    isCompact = false,
    isDisabled = false,
    nbDecimal,
    remainingWeight,
    text,
    value,
}: DistributionCaseProposalProps) => {
    const textId = useId();
    const descriptionId = useId();

    const [localValue, setLocalValue] = useState<
        number | string | undefined | ""
    >(value);

    //After reset the vote, need this ⬇️, for reset each input
    useEffect(() => {
        setLocalValue(value !== undefined ? value.toString() : "");
    }, [value]);

    const handleChange = (e: { target: HTMLElement & { value: string } }) => {
        const input = e.target;

        // On a mobile with default arrow keys, the focus is not always forced
        // This forces the focus as it is needed to get the onBlur function called
        if (document.activeElement !== input) {
            input.focus();
        }

        const rawValue = input.value;

        if (!/^[0-9]*[.,]?[0-9]*$/.test(rawValue) && rawValue !== "") {
            return;
        }

        if (rawValue === "") {
            setLocalValue("");
            return;
        }

        const normalized: string = rawValue.replace(",", ".");
        const hasDot: boolean = normalized.includes(".");
        const [intPart, decPart = ""] = normalized.split(".");

        const cleanedIntPart = String(parseInt(intPart, 10));

        const cleanedValue: string = decPart
            ? `${cleanedIntPart}.${decPart.slice(0, nbDecimal)}`
            : cleanedIntPart + (hasDot && nbDecimal > 0 ? "." : "");

        const numericValue: number = parseFloat(cleanedValue);
        if (isNaN(numericValue) || numericValue < 0) return;

        const maxValue: number = roundDecimal(
            remainingWeight + Number(value),
            nbDecimal,
        );
        setLocalValue(numericValue >= maxValue ? maxValue : cleanedValue);
    };

    const proposalImage = image ? (
        <ProposalImage
            src={image}
            alt=""
            $isActive={isActive}
            $isCompact={isCompact}
            $isDisabled={isDisabled}
        />
    ) : null;
    const proposalDescription = description ? (
        <DescriptionText
            id={descriptionId}
            $isActive={isActive}
            $isDisabled={isDisabled}
        >
            {description}
        </DescriptionText>
    ) : null;
    const boxSize = getBoxSize({ color, isCompact });
    // The description, then the help on the Enter key.
    const describedByIds = [description ? descriptionId : null, hintId]
        .filter(Boolean)
        .join(" ");

    return (
        <StyledWrapper
            $isActive={isActive}
            $color={color}
            $isCompact={isCompact}
            $isDisabled={isDisabled}
        >
            <LabelGroup
                data-option-content
                $boxPosition={boxPosition}
                $isCompact={isCompact}
            >
                <SelectionBox
                    color={color}
                    isActive={isActive}
                    isDisabled={isDisabled}
                    size={boxSize}
                />
                {proposalImage}
                <TextWrapper data-option-text $isCompact={isCompact}>
                    <StyledPropositionsText id={textId}>
                        {text}
                    </StyledPropositionsText>
                    {proposalDescription}
                </TextWrapper>
            </LabelGroup>
            <Input
                type="text"
                inputMode="decimal"
                value={localValue || ""}
                onBlur={() => {
                    handleBlur(Number(localValue));
                }}
                onChange={handleChange}
                disabled={isDisabled}
                $isActive={isActive}
                $isCompact={isCompact}
                $isDisabled={isDisabled}
                data-testactive={isActive}
                aria-labelledby={textId}
                aria-describedby={describedByIds || undefined}
                onKeyUp={handleKeyUp}
                ref={currentRef}
            />
        </StyledWrapper>
    );
};

export default DistributionCaseProposal;
